// src/pages/CertificatePage.tsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { useAuth } from '../context/AuthContext';
import { getUserCertificate } from '../services/authService';
import { generateCertificatePDF } from '../services/pdfService';
import { supabase } from '../services/db';

const CertificatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [certData, setCertData] = useState<any | null>(location.state?.certData);
  const [allCertificates, setAllCertificates] = useState<any[]>([]);
  const [selectedCertIndex, setSelectedCertIndex] = useState(0);
  const [pdfDataUri, setPdfDataUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assetsLoaded, setAssetsLoaded] = useState(false);

  useEffect(() => {
    const loadCertificate = async () => {
      if (!isAuthenticated || !user) {
        navigate('/login', { replace: true });
        return;
      }

      if (certData) {
        setAllCertificates([certData]);
        setLoading(false);
        return;
      }

      try {
        const fetchedCert = await getUserCertificate(user.id);
        if (fetchedCert) {
          setCertData(fetchedCert);
          setAllCertificates([fetchedCert]);
        } else {
          setError('Kein Zertifikat gefunden. Bitte stellen Sie sicher, dass Sie den Test bestanden haben.');
        }
      } catch (err) {
        console.error('Error fetching certificate:', err);
        setError('Fehler beim Laden des Zertifikats.');
      } finally {
        setLoading(false);
      }
    };

    loadCertificate();
  }, [location.state?.certData, user, isAuthenticated, navigate, certData]);

  // Load assets and generate PDF preview
  useEffect(() => {
    const loadAssetsAndGeneratePDF = async () => {
      if (!certData) {
        return;
      }

      try {
        const doc = await generateCertificatePDF({
          name: certData.name,
          company: certData.company,
          number: certData.number,
          date: certData.date
        });
        const pdfDataUri = doc.output('datauristring');
        setPdfDataUri(pdfDataUri);
        setAssetsLoaded(true);
      } catch (error) {
        console.error('Error generating PDF preview:', error);
        setError('Fehler beim Generieren der PDF-Vorschau');
      }
    };

    loadAssetsAndGeneratePDF();
  }, [certData]);


  const generatePDF = async () => {
    if (!certData) {
      alert('Zertifikatsdaten nicht verfügbar.');
      return;
    }
    
    if (!assetsLoaded) {
      alert('PDF wird noch generiert. Bitte warten Sie einen Moment.');
      return;
    }

    // Use shared PDF generation service
    const doc = await generateCertificatePDF({
      name: certData.name,
      company: certData.company,
      number: certData.number,
      date: certData.date
    });

    doc.save(`Zertifikat_${certData.name.replace(/\s+/g, '_')}.pdf`);
  };

  if (loading || !pdfDataUri) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Zertifikat wird generiert...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Fehler</h1>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/kurszugang')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
            >
              Zurück zum Kurszugang
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!certData) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Zertifikat nicht verfügbar</h1>
            <p className="text-gray-700 mb-6">
              Es konnten keine Zertifikatsdaten gefunden werden. Bitte stellen Sie sicher, dass Sie den Abschlusstest bestanden haben.
            </p>
            <button
              onClick={() => navigate('/kurszugang')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
            >
              Zurück zum Kurszugang
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Ihr KI-Kompetenznachweis
          </h1>
          
          {/* Certificate Selection */}
          {allCertificates.length > 1 && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Zertifikat auswählen</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allCertificates.map((cert, index) => (
                  <button
                    key={cert.id}
                    onClick={() => {
                      setSelectedCertIndex(index);
                      setCertData(cert);
                      setPdfDataUri(null); // Reset PDF to trigger regeneration
                    }}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedCertIndex === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium text-gray-800">{cert.number}</div>
                    <div className="text-sm text-gray-600">Ausgestellt: {cert.date}</div>
                    {index === 0 && (
                      <div className="text-xs text-green-600 font-medium mt-1">Neuestes</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* PDF Preview */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Zertifikat-Vorschau
              {allCertificates.length > 1 && certData && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  ({certData.number})
                </span>
              )}
            </h2>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <iframe
                src={pdfDataUri}
                width="100%"
                height="600px"
                frameBorder="0"
                title="Certificate Preview"
                className="w-full"
              />
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-center space-x-4 flex-wrap gap-2">
            <button
              onClick={generatePDF}
              disabled={!assetsLoaded}
              className={`px-6 py-2 rounded-md transition-colors ${
                assetsLoaded 
                  ? 'bg-[#2574B5] text-white hover:bg-blue-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {assetsLoaded ? 'PDF herunterladen' : 'PDF wird generiert...'}
            </button>
            <button
              onClick={() => navigate('/kurszugang')}
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors"
            >
              Zurück
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;